import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Box, Typography } from "@material-ui/core";

import themeColorsDesingSystem from "assets/theme/ds-colors";

import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import useStructureQueries from "hooks/useStructureQueries";

import {
  updateRepresentative,
  updateRepresentativeFilters,
} from "redux/reducers/criarVitrineReducer";

import { DEFAULT_REPRESENTATIVE_FILTERS } from "../utils/wizard-helpers";
import { buildRepresentativePagedQuery } from "../../shared/utils/representative-api-params";
import { REPRESENTATIVE_PAGE_SIZE } from "../../shared/utils/representative-helpers";

import RepresentativeFilters from "../../shared/components/representative-filters";
import RepresentativeSelectionList, {
  useRepresentativeListSearch,
} from "../../shared/components/representative-selection-list";

export default function TargetAudience() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [grupoEconomicoOptions, setGrupoEconomicoOptions] = useState([]);
  const [razaoSocialOptions, setRazaoSocialOptions] = useState([]);
  const loadedMarcaRef = useRef(null);

  const dispatch = useDispatch();
  const snackbar = useSnackbar();
  const { getEconomicGroups, getStores } = useStructureQueries();

  const {
    formData,
    representatives = [],
    representativeFilters,
  } = useSelector((state) => state.criarVitrine);

  const filters = useMemo(
    () => ({
      ...DEFAULT_REPRESENTATIVE_FILTERS,
      ...representativeFilters,
    }),
    [representativeFilters],
  );

  const idMarca = formData?.brand?.value;

  const selectedIds = useMemo(
    () => new Set(representatives.map((r) => r.Id)),
    [representatives],
  );

  const fetchPage = useCallback(
    async (pageToLoad, filtersToUse) => {
      if (!idMarca) {
        setItems([]);
        setTotalItems(0);
        setTotalPages(0);
        return;
      }

      setIsLoading(true);
      try {
        const query = buildRepresentativePagedQuery(filtersToUse, {
          idMarca,
          page: pageToLoad,
          pageSize: REPRESENTATIVE_PAGE_SIZE,
        });

        const response = await Api.get(
          "list-b2b-showcase-representatives",
          "showcaseB2B",
          `?${query}`,
          true,
        );

        const result = response?.Result;
        const list = (result?.Lista ?? result?.Result ?? []).map((rep) => ({
          ...rep,
          __selectionId: rep.Id,
        }));

        setItems(list);
        setTotalItems(result?.TotalRegistros ?? 0);
        setTotalPages(result?.TotalDePaginas ?? 0);
        setPage(result?.Pagina ?? pageToLoad);
      } catch (error) {
        console.error(error);
        setItems([]);
        setTotalItems(0);
        setTotalPages(0);
        snackbar.showMessage(
          "Não foi possível carregar os representantes. Tente novamente.",
          { severity: "error" },
        );
      } finally {
        setIsLoading(false);
      }
    },
    [idMarca, snackbar],
  );

  const loadFilterOptions = useCallback(async () => {
    if (!idMarca) {
      setGrupoEconomicoOptions([]);
      setRazaoSocialOptions([]);
      return;
    }

    try {
      const [groupsRes, storesRes] = await Promise.all([
        getEconomicGroups({ idMarca }),
        getStores({ idMarca }),
      ]);

      const groups = (groupsRes?.data ?? []).map((g) => ({
        Id: g.Id ?? g.id,
        Nome: g.Nome ?? g.nome,
      }));
      const stores = (storesRes?.data ?? []).map((s) => ({
        Id: s.Id ?? s.id,
        Nome: (s.Nome ?? s.nome ?? s.RazaoSocial ?? "").trim(),
      }));

      setGrupoEconomicoOptions(groups);
      setRazaoSocialOptions(stores);
    } catch {
      setGrupoEconomicoOptions([]);
      setRazaoSocialOptions([]);
    }
  }, [getEconomicGroups, getStores, idMarca]);

  const handleFiltersChange = useCallback(
    (updates) => {
      dispatch(updateRepresentativeFilters(updates));
      setPage(0);
    },
    [dispatch],
  );

  const onDebouncedSearch = useCallback(
    (value) => {
      dispatch(updateRepresentativeFilters({ search: value }));
      setPage(0);
    },
    [dispatch],
  );

  const { searchInput, handleSearchChange } = useRepresentativeListSearch(
    filters.search ?? "",
    onDebouncedSearch,
  );

  useEffect(() => {
    if (!idMarca) {
      loadedMarcaRef.current = null;
      setItems([]);
      return;
    }
    if (loadedMarcaRef.current !== idMarca) {
      loadedMarcaRef.current = idMarca;
      loadFilterOptions();
    }
  }, [idMarca, loadFilterOptions]);

  useEffect(() => {
    fetchPage(page, filters);
  }, [fetchPage, page, filters]);

  const visibleItems = items;
  const allVisibleSelected =
    visibleItems.length > 0 &&
    visibleItems.every((r) => selectedIds.has(r.__selectionId));
  const someVisibleSelected = visibleItems.some((r) =>
    selectedIds.has(r.__selectionId),
  );

  const handleToggleItem = useCallback(
    (item) => {
      const id = item.__selectionId;
      if (selectedIds.has(id)) {
        dispatch(
          updateRepresentative(representatives.filter((r) => r.Id !== id)),
        );
      } else {
        const { __selectionId, ...rep } = item;
        dispatch(updateRepresentative([...representatives, rep]));
      }
    },
    [dispatch, representatives, selectedIds],
  );

  const handleToggleAllVisible = useCallback(
    (visibleIds) => {
      if (allVisibleSelected) {
        const hide = new Set(visibleIds);
        dispatch(
          updateRepresentative(representatives.filter((r) => !hide.has(r.Id))),
        );
      } else {
        const map = new Map(representatives.map((r) => [r.Id, r]));
        visibleItems.forEach((item) => {
          const { __selectionId, ...rep } = item;
          map.set(__selectionId, rep);
        });
        dispatch(updateRepresentative(Array.from(map.values())));
      }
    },
    [allVisibleSelected, dispatch, representatives, visibleItems],
  );

  return (
    <Box>
      <RepresentativeFilters
        isLoading={isLoading}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        grupoEconomicoOptions={grupoEconomicoOptions}
        razaoSocialOptions={razaoSocialOptions}
      />

      <RepresentativeSelectionList
        isLoading={isLoading}
        items={items}
        totalItems={totalItems}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        selectedIds={selectedIds}
        onToggleItem={handleToggleItem}
        onToggleAllVisible={handleToggleAllVisible}
        allVisibleSelected={allVisibleSelected}
        someVisibleSelected={someVisibleSelected}
        searchValue={searchInput}
        onSearchChange={handleSearchChange}
        emptyMessage="Nenhum representante encontrado com os filtros aplicados."
        selectionFooter={
          representatives.length > 0 ? (
            <Typography
              variant="caption"
              style={{
                display: "block",
                marginTop: 8,
                color: themeColorsDesingSystem?.gray?.dark,
              }}
            >
              {representatives.length} representante(s) selecionado(s)
            </Typography>
          ) : null
        }
      />
    </Box>
  );
}
